import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import Footer from "./footer";
import StockDetails from "./stockDetails";
import { createEmptyStock, formatStockFromRaw, getChapterHighlightStyle } from "./stockUtils";
import {
    fetchStockDataWithFallback,
    getRecentDateStrings,
    fetchLatestFilterHistory,
} from "./dataFetcher";

import "./body.css";
import "./fave.css";

const formatPrettyDate = (dateStr) => {
    const parts = (dateStr || "").split("-");
    if (parts.length !== 3) return dateStr;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);

    const parsed = new Date(year, month, day);
    if (Number.isNaN(parsed.getTime())) {
        return dateStr;
    }

    return parsed.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

function Business() {
    const [isReady, setIsReady] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const { ticker } = useParams();
    const [business, setBusiness] = useState(() => createEmptyStock());
    const [latestFilterEntry, setLatestFilterEntry] = useState(null);
    const [chapterHighlights, setChapterHighlights] = useState({
        ch14Success: false,
        ch15Success: false,
    });
    const [dataDate, setDataDate] = useState("");

    useEffect(() => {
        window.scrollTo(0, 0);

        const load = async () => {
            try {
                const { combined, dateStr } = await fetchStockDataWithFallback(getRecentDateStrings(4));

                const target = ticker.trim().toLowerCase();
                const currentBusiness = combined.find(
                    (stock) => (stock.ticker || "").toLowerCase() === target
                );

                if (!currentBusiness) {
                    setNotFound(true);
                    setIsReady(true);
                    return;
                }

                setDataDate(formatPrettyDate(currentBusiness.dataDate || dateStr));

                const latestFilterRecord = await fetchLatestFilterHistory(dateStr, target);
                setLatestFilterEntry(latestFilterRecord);
                if (
                    latestFilterRecord &&
                    latestFilterRecord.detail &&
                    typeof latestFilterRecord.detail === "object"
                ) {
                    const chapter14Text = latestFilterRecord.detail["Chapter 14 Analysis"] || "";
                    const chapter15Text = latestFilterRecord.detail["Chapter 15 Analysis"] || "";

                    const normalize = (text = "") =>
                        (typeof text === "string" ? text : "")
                            .trim()
                            .toUpperCase()
                            .startsWith("SUCCESS");

                    setChapterHighlights({
                        ch14Success: normalize(chapter14Text),
                        ch15Success: normalize(chapter15Text),
                    });
                } else {
                    setChapterHighlights({ ch14Success: false, ch15Success: false });
                }

                setBusiness(formatStockFromRaw(currentBusiness));
                setIsReady(true);
            } catch (err) {
                console.error("Unable to load business data:", err);
            }
        };

        load();
    }, []);

    useEffect(() => {
        document.title = `Invest With Knowledge! - ${business.name}`;
    }, [business.name]);

    const sectionClass = `stockdiv ${isReady ? "fade-in" : ""}`;
    const basicInfoStyle = useMemo(
        () => getChapterHighlightStyle(chapterHighlights),
        [chapterHighlights]
    );

    const extractStatus = (text = "") => {
        const normalized = (typeof text === "string" ? text : "").trim().toUpperCase();
        if (normalized.startsWith("SUCCESS")) return "SUCCESS";
        if (normalized.startsWith("FAIL")) return "FAIL";
        return "";
    };

    const extractReasons = (text = "") => {
        const value = typeof text === "string" ? text : "";
        const trimmed = value.trim();
        const normalized = trimmed.toUpperCase();

        if (normalized.startsWith("SUCCESS")) return [];
        if (normalized.startsWith("FAIL")) {
            const rest = trimmed.replace(/^FAIL(?:\s*:)?\s*/i, "").trim();
            if (!rest) return [];

            const cleaned = rest
                .replace(/\s*&\s*/g, " ")
                .replace(/\s*[\u2022•]\s*/g, " ")
                .replace(/\s*\n+\s*/g, " ")
                .trim();

            const splitByFailed = cleaned
                .split(/(?=Failed\b)/gi)
                .map((r) => r.trim().replace(/^[,;|.-\s]+/, ""))
                .filter((r) => r.length > 0);

            return splitByFailed.length ? splitByFailed : [cleaned];
        }
        return [];
    };

    const StatusStyled = ({ status }) => {
        if (status === "SUCCESS") {
            return (
                <p className="centeredp" style={{ color: "var(--positive)", fontWeight: "bold" }}>
                    SUCCESS
                </p>
            );
        }
        if (status === "FAIL") {
            return (
                <p className="centeredp" style={{ color: "var(--negative)", fontWeight: "bold" }}>
                    FAIL
                </p>
            );
        }
        return null;
    };

    const renderReasons = (reasons) => {
        if (!reasons.length) return null;

        return (
            <div style={{ marginTop: "6px" }}>
                {reasons.map((reason, idx) => (
                    <p key={idx}>
                        &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;{reason}
                    </p>
                ))}
            </div>
        );
    };

    return (
        <>
            <section className="mainbody">
                <h2 className="responsiveheader">Business Details</h2>

                {!isReady ? (
                    <div className="archivediv loadercontainer">
                        <p className="centeredp" aria-live="polite">
                            Loading business details
                            <span className="loadingdots" aria-hidden="true">
                                <span className="dot">.</span>
                                <span className="dot">.</span>
                                <span className="dot">.</span>
                            </span>
                        </p>
                    </div>
                ) : notFound ? (
                    <div className="archivediv loadercontainer">
                        <p className="centeredp">We don't have data for <strong>{ticker.toUpperCase()}</strong>.</p>
                    </div>
                ) : (
                    <>
                        <StockDetails
                            stock={business}
                            sectionClass={sectionClass}
                            highlightStyle={basicInfoStyle}
                        />

                        {/* HISTORY */}
                        <div className={`stockdiv ${isReady ? "fade-in" : ""}`} style={basicInfoStyle}>
                            <p className="centeredp">I.W.K. Status</p>

                            {latestFilterEntry && latestFilterEntry.detail ? (
                                (() => {
                                    const chapter14 =
                                        latestFilterEntry.detail["Chapter 14 Analysis"] || "";
                                    const chapter15 =
                                        latestFilterEntry.detail["Chapter 15 Analysis"] || "";

                                    const ch14_status = extractStatus(chapter14);
                                    const ch14_reasons = extractReasons(chapter14);

                                    const ch15_status = extractStatus(chapter15);
                                    const ch15_reasons = extractReasons(chapter15);

                                    return (
                                        <div className="archivediv">
                                            {/* ==== Chapter 14 ==== */}
                                            <p className="centeredp">Chapter 14 Analysis:</p>
                                            <StatusStyled status={ch14_status} />
                                            {renderReasons(ch14_reasons)}

                                            {/* ==== Chapter 15 ==== */}
                                            <p className="centeredp" style={{ marginTop: "12px" }}>
                                                Chapter 15 Analysis:
                                            </p>
                                            <StatusStyled status={ch15_status} />
                                            {renderReasons(ch15_reasons)}
                                        </div>
                                    );
                                })()
                            ) : (
                                <p className="centeredp">Not Enough Data Available.</p>
                            )}
                        </div>
                        <p className="centeredp" style={{ marginTop: "16px" }}>
                            Stock data last updated on: {dataDate || "Unknown"}
                        </p>
                    </>
                )}
            </section>
            <Footer />
        </>
    );
}

export default Business;
